import { Box, Grid, SxProps, Typography } from "@mui/material";
import StarsIcon from "@mui/icons-material/Stars";
import ChangeHistoryTwoToneIcon from "@mui/icons-material/ChangeHistoryTwoTone";
import { PageTitle } from "../../layoutPages/components/PageTitle";
import { MainCard, MainCardProps } from "../../layoutPages/components/MainCard";
import { Paragraph, ParagraphProps } from "../../layoutPages/containers/Paragraphs/Paragraph";
import { DottedList } from "../../layoutPages/containers/DottedList";
import { FlexPhotoRow } from "../../layoutPages/components/FlexPhotoRow";
import { MenuButtonData } from "../../system/Classes/MenuButtonData";
import { CourseTab } from "./menuTab";

/**
 * CourseTarget 頁面用五種 block:
 *  - paragraph : 一個文字段落(可選 title、可選圖片、可調圖位置/大小)
 *  - heading   : 一個標題(可選顏色 / icon)
 *  - photoRow  : 一句說明 + 一排相(例如得獎花絮),重複出現嘅組合用呢個
 *  - mainCard  : 單張圖卡(可點擊放大、hover 效果)
 *  - custom    : 任何手寫 JSX(特殊排版逃生門)
 *
 * 常用版面設定:
 *   ownGrid / gridXs / gridSm : 呢個 block 自己佔一個 Grid(例如要窄一點嘅圖)
 *   position                  : 圖喺文字嘅 left | right | top | down
 *   imageHeight               : 圖高,例如 "350px"
 */

export type ImagePosition = "left" | "right" | "top" | "down";

/** 條列樣式:number = 1. 2. 3.;stars = 星星;triangle = 三角形;default/undefined = 圓點 */
export type ListVariantKey = "number" | "stars" | "triangle";

type GridOpts = {
    ownGrid?: boolean;
    gridXs?: number;
    gridSm?: number;
};

export type ParagraphBlock = GridOpts & {
    type: "paragraph";
    title?: string | React.ReactNode;
    titleSize?: ParagraphProps["titleSize"];
    data: Array<string | React.ReactNode>;
    /** 想用星星/數字條列時,包一層 DottedList(見 starsList / numberedList) */
    listVariant?: ListVariantKey;
    images?: string[];
    position?: ImagePosition;
    imageHeight?: string;
    mainCardProps?: MainCardProps;
    sx?: SxProps;
};

export type HeadingBlock = GridOpts & {
    type: "heading";
    text: string;
    variant?: "h3" | "h4" | "h5" | "h6" | "body1" | "body2";
    color?: string;
    icon?: "stars" | "triangle";
    sx?: SxProps;
};

export type PhotoRowBlock = GridOpts & {
    type: "photoRow";
    /** 相片上方嘅一句說明(可選);想用星星/數字條列就加 listVariant */
    text?: string;
    images: string[];
    height?: string;
    listVariant?: ListVariantKey;
    /** 相片橫向分佈方式(預設 center) */
    justifyContent?: "center" | "flex-start" | "flex-end" | "space-between" | "space-around" | "space-evenly";
    gap?: number | string;
};

export type MainCardBlock = GridOpts & {
    type: "mainCard";
    src: string;
    imgHeight?: string;
    imgSx?: SxProps;
    sx?: SxProps;
    /** true = 唔彈出大圖;唔填就同原本 MainCard 一樣會彈 */
    noImgPopup?: boolean;
    noHoverAni?: boolean;
    onClick?: () => void;
};

export type CustomBlock = GridOpts & {
    type: "custom";
    node: React.ReactNode;
};

export type CourseBlock = ParagraphBlock | HeadingBlock | PhotoRowBlock | MainCardBlock | CustomBlock;

export function starsList(data: Array<string | React.ReactNode>) {
    return <DottedList data={data} variant={<StarsIcon fontSize="small" />} />;
}

export function numberedList(data: Array<string | React.ReactNode>) {
    return <DottedList data={data} variant="number" />;
}

function resolveListVariant(variant?: ListVariantKey): React.ReactNode | "number" | undefined {
    if (!variant) return undefined;
    if (variant === "number") return "number";
    if (variant === "stars") return <StarsIcon fontSize="small" />;
    if (variant === "triangle") return <ChangeHistoryTwoToneIcon fontSize="small" />;
    return undefined;
}

function CourseBlockView({ block }: { block: CourseBlock }) {
    switch (block.type) {
        case "heading": {
            const iconNode =
                block.icon === "stars" ? (
                    <StarsIcon sx={{ mr: 1, verticalAlign: "middle", color: block.color ?? "#F02602" }} />
                ) : block.icon === "triangle" ? (
                    <ChangeHistoryTwoToneIcon
                        sx={{ mr: 0.5, verticalAlign: "middle", color: block.color ?? "#F02602" }}
                    />
                ) : null;
            return (
                <Typography
                    variant={block.variant ?? "h4"}
                    align="left"
                    sx={{ py: 2, color: block.color, ...((block.sx as object) ?? {}) }}
                >
                    {iconNode}
                    {block.text}
                </Typography>
            );
        }
        case "paragraph": {
            const data = block.listVariant
                ? [<DottedList data={block.data} variant={resolveListVariant(block.listVariant)} />]
                : block.data;
            return (
                <Paragraph
                    title={block.title}
                    titleSize={block.titleSize}
                    data={data}
                    sx={block.sx}
                    haveImage={
                        block.images && block.images.length > 0
                            ? {
                                  images: block.images.map((src) => ({ src })),
                                  position: block.position,
                                  imageHeight: block.imageHeight,
                                  mainCardProps: block.mainCardProps,
                              }
                            : undefined
                    }
                />
            );
        }
        case "photoRow":
            return (
                <Box sx={{ my: 2 }}>
                    {block.text && (
                        <DottedList data={[block.text]} variant={resolveListVariant(block.listVariant)} />
                    )}
                    {block.images.length > 0 && (
                        <FlexPhotoRow
                            height={block.height ?? "450px"}
                            images={block.images}
                            justifyContent={block.justifyContent}
                            gap={block.gap}
                        />
                    )}
                </Box>
            );
        case "mainCard":
            return (
                <Box sx={block.sx}>
                    <MainCard
                        src={block.src}
                        imgHeight={block.imgHeight}
                        imgSx={block.imgSx}
                        noImgPopup={block.noImgPopup}
                        noHoverAni={block.noHoverAni}
                        onClick={block.onClick}
                    />
                </Box>
            );
        case "custom":
            return <>{block.node}</>;
        default:
            return null;
    }
}

export function CourseBlocks({ blocks }: { blocks: CourseBlock[] }) {
    return (
        <>
            {blocks.map((block, index) => (
                <CourseBlockView key={index} block={block} />
            ))}
        </>
    );
}

export interface CoursePageLayoutProps {
    title: string;
    subtitle: string;
    menuData: Array<MenuButtonData>;
    nowIndex: number;
    topImage?: {
        src: string;
        imgHeight?: string;
        noImgPopup?: boolean;
    };
    blocks: CourseBlock[];
}

export function CoursePageLayout(props: CoursePageLayoutProps) {
    const groups: Array<{
        ownGrid: boolean;
        gridXs: number;
        gridSm: number;
        blocks: CourseBlock[];
    }> = [];

    for (const block of props.blocks) {
        if (block.ownGrid) {
            groups.push({
                ownGrid: true,
                gridXs: block.gridXs ?? 11,
                gridSm: block.gridSm ?? 10,
                blocks: [block],
            });
            continue;
        }
        const last = groups[groups.length - 1];
        if (last && !last.ownGrid) {
            last.blocks.push(block);
        } else {
            groups.push({ ownGrid: false, gridXs: 11, gridSm: 10, blocks: [block] });
        }
    }

    return (
        <Grid container direction="row" justifyContent="center" alignItems="top" spacing={2}>
            <PageTitle title={props.title} size="h3" subtitle={{ title: props.subtitle, size: "h5" }} />
            <Grid item xs={11} sm={10}>
                <CourseTab data={props.menuData} nowIndex={props.nowIndex} />
                {props.topImage && (
                    <MainCard
                        src={props.topImage.src}
                        imgHeight={props.topImage.imgHeight}
                        noImgPopup={props.topImage.noImgPopup ?? true}
                    />
                )}
            </Grid>
            {groups.map((group, index) => (
                <Grid key={index} item xs={group.gridXs} sm={group.gridSm} sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <CourseBlocks blocks={group.blocks} />
                    </Box>
                </Grid>
            ))}
        </Grid>
    );
}
